import React from 'react';
import { motion, Variants } from 'framer-motion';

interface SectionTransitionProps {
  children: React.ReactNode;
  sectionIndex: number;
  sectionKey: string;
  theme: 'light' | 'dark';
}

// Enhanced section variants with improved animations
const sectionVariants: Variants = {
  initial: { 
    opacity: 0, 
    y: 30,
    scale: 0.98
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: { 
      duration: 1.2, 
      ease: [0.23, 1, 0.32, 1],
      staggerChildren: 0.15
    }
  },
  exit: { 
    opacity: 0, 
    y: -15,
    scale: 1.02,
    transition: { 
      duration: 0.8,
      ease: [0.76, 0, 0.24, 1]
    }
  }
};

const heroVariants: Variants = {
  initial: { 
    opacity: 0, 
    scale: 1.1 
  },
  animate: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      duration: 1.2,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    transition: { 
      duration: 0.6,
      ease: "easeIn"
    }
  }
};

const aboutVariants: Variants = {
  initial: { 
    opacity: 0, 
    y: 60
  },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    y: -30,
    transition: { 
      duration: 0.5,
      ease: "easeIn"
    }
  }
};

const visionVariants: Variants = {
  initial: { 
    opacity: 0, 
    x: 100,
    scale: 0.9
  },
  animate: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: { 
      duration: 0.9,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    x: -50,
    scale: 1.05,
    transition: { 
      duration: 0.4,
      ease: "easeIn"
    }
  }
};

// Get appropriate variants based on section type
const getVariants = (sectionKey: string): Variants => {
  switch (sectionKey) {
    case 'hero':
      return heroVariants;
    case 'about':
    case 'about-sectors':
      return aboutVariants;
    case 'vision':
      return visionVariants;
    default:
      return sectionVariants;
  }
};

// Background particle effect for enhanced transitions
const BackgroundParticles: React.FC<{ theme: 'light' | 'dark' }> = ({ theme }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className={`absolute w-1 h-1 rounded-full ${
            theme === 'dark' ? 'bg-white/20' : 'bg-primary/20'
          }`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            opacity: [0, 0.6, 0],
            scale: [0, 1.5, 0],
            y: [0, -100],
          }}
          transition={{
            duration: 4 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "linear"
          }}
        />
      ))}
    </div>
  );
};

export const SectionTransition: React.FC<SectionTransitionProps> = ({
  children,
  sectionIndex,
  sectionKey,
  theme
}) => {
  const variants = getVariants(sectionKey);

  return (
    <motion.div
      className="relative w-full h-full"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Background particles for enhanced visual effect */}
      {sectionKey === 'hero' && <BackgroundParticles theme={theme} />}
      
      {/* Section content with enhanced mobile spacing */}
      <div className="relative w-full h-full">
        {children}
      </div>
      
      {/* Smooth gradient transition border */}
      <motion.div
        className={`absolute bottom-0 left-0 right-0 h-px ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-transparent via-white/30 to-transparent' 
            : 'bg-gradient-to-r from-transparent via-primary/30 to-transparent'
        }`}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      />
    </motion.div>
  );
};

export default SectionTransition;